import * as btc from '@scure/btc-signer'
import { WalletProvider } from '@/utils/types/wallet'
import {
	AppConfig,
	UserSession,
	showConnect,
	openPsbtRequestPopup
} from '@stacks/connect'
import { StacksMainnet } from '@stacks/network'

import SafeLocalStorage from '../safe-local-storage'
import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_XVERSE_PAYMENT_ADDRESS,
	STORAGE_WALLET_TYPE,
	STORAGE_PUBLIC_KEY,
	STORAGE_XVERSE_TAPROOT_PUBLIC_KEY,
	NETWORK
} from '@/utils/constants'
import Wallet from './'
import { linkChildToSignedParent } from './chain'
import {
	allInputIndexes,
	dummyNativeSegwitPsbt,
	dummyTaprootPsbt,
	extractRawHex,
	finalizeInputs,
	finalizeTaprootKeyPath,
	inputScriptHex,
	inputTxidHex,
	parsePsbtTransaction,
	toPsbtHex
} from './psbt'

const appConfig = new AppConfig()
const userSession = new UserSession({ appConfig })

/** Leather signs one key per popup: the payment (P2WPKH) key or the ordinals (P2TR) key. */
type LeatherKey = 'balance' | 'taproot'

/** The API marks purchase dummy inputs with exactly this many sats. */
const DUMMY_INPUT_SATS = 600n

/** Every sighash Leather may be asked for: default, ALL, ALL|ACP, SINGLE|ACP. */
const ANY_SIGHASH = [0, 1, 0x81, 0x83]

export default class HiroWallet implements WalletProvider {
	get name(): string {
		return 'Leather'
	}

	get capabilities(): string[] {
		return ['payments-wallet']
	}

	get enabled(): boolean {
		return NETWORK === 'BTC'
	}

	resolve(userData: any) {
		const profile = userData?.profile
		SafeLocalStorage.setItem(STORAGE_PUBLIC_KEY, profile?.btcPublicKey?.p2wpkh)
		SafeLocalStorage.setItem(STORAGE_XVERSE_PAYMENT_ADDRESS, profile?.btcAddress?.p2wpkh?.mainnet)
		SafeLocalStorage.setItem(STORAGE_XVERSE_TAPROOT_PUBLIC_KEY, profile?.btcPublicKey?.p2tr)
		SafeLocalStorage.setItem(STORAGE_TAPROOT_ADDRESS, profile?.btcAddress?.p2tr?.mainnet)
		SafeLocalStorage.setItem(STORAGE_WALLET_TYPE, 'hiro')
	}

	get connectIcon(): string {
		return '/connect-leather.svg'
	}

	async connect(): Promise<boolean> {
		// @ts-ignore
		if (!window.HiroWalletProvider) {
			window.open('https://leather.io/', '_blank')
			return false
		}

		await this.login()
		return true
	}

	async login(): Promise<void> {
		showConnect({
			userSession,
			// @ts-ignore
			network: StacksMainnet,
			appDetails: {
				name: 'Ordinals Wallet',
				icon: 'https://ordinalswallet.com/logo-icon.svg'
			},
			onFinish: () => {
				const userData = userSession.loadUserData()
				this.resolve(userData)
				Wallet.fetchProfile()
			},
			onCancel: () => {
				// handle if user closed connection prompt
			}
		})
	}

	balanceAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_XVERSE_PAYMENT_ADDRESS)
	}

	balancePublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}

	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	taprootPublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_XVERSE_TAPROOT_PUBLIC_KEY)
	}

	async dummyPsbt(): Promise<string> {
		return dummyTaprootPsbt(this.taprootPublicKey())
	}

	async dummyBalancePsbt(): Promise<string> {
		return dummyNativeSegwitPsbt(this.balancePublicKey())
	}

	private publicKeyFor(key: LeatherKey): string {
		return key === 'balance' ? this.balancePublicKey() : this.taprootPublicKey()
	}

	/**
	 * One Leather popup: sign `indexes` with one key. Resolves with the
	 * wallet's PSBT parsed; the caller decides what to finalize.
	 */
	private requestSignature(
		tx: btc.Transaction,
		key: LeatherKey,
		indexes: number[],
		allowedSighash: number[]
	): Promise<btc.Transaction> {
		return new Promise((res, rej) => {
			openPsbtRequestPopup({
				publicKey: this.publicKeyFor(key),
				hex: toPsbtHex(tx),
				allowedSighash,
				signAtIndex: indexes,
				userSession,
				network: new StacksMainnet(),
				onFinish: (e: any) => {
					try {
						res(parsePsbtTransaction(e.hex))
					} catch (error) {
						rej(error)
					}
				},
				onCancel: () => rej('User canceled')
			} as any)
		})
	}

	/** Which of our two keys owns each input, by its prevout script. */
	private ownedInputs(tx: btc.Transaction): { balance: number[]; taproot: number[] } {
		const scriptOf = (address: string | null) => {
			if (!address) return null
			try {
				return Buffer.from(btc.OutScript.encode(btc.Address().decode(address))).toString('hex')
			} catch {
				return null
			}
		}
		const balanceScript = scriptOf(this.balanceAddress())
		const taprootScript = scriptOf(this.taprootAddress())
		const owned = { balance: [] as number[], taproot: [] as number[] }
		for (const i of allInputIndexes(tx)) {
			const script = inputScriptHex(tx, i)
			if (script && script === balanceScript) owned.balance.push(i)
			else if (script && script === taprootScript) owned.taproot.push(i)
		}
		return owned
	}

	/**
	 * Sign whatever we own with whichever key owns it (one popup per key),
	 * finalize those inputs, and return the PSBT. Offers, dummy signatures
	 * and anything else that goes through the facade's signPSBT land here.
	 */
	async signPSBT(psbt: string): Promise<string> {
		let tx = parsePsbtTransaction(psbt)
		const owned = this.ownedInputs(tx)
		if (owned.taproot.length) {
			tx = await this.requestSignature(tx, 'taproot', owned.taproot, ANY_SIGHASH)
		}
		if (owned.balance.length) {
			tx = await this.requestSignature(tx, 'balance', owned.balance, ANY_SIGHASH)
		}
		finalizeInputs(tx, [...owned.taproot, ...owned.balance])
		return toPsbtHex(tx)
	}

	async signPSBTExtractTx(psbt: string): Promise<string> {
		return extractRawHex(parsePsbtTransaction(await this.signPSBT(psbt)))
	}

	/** Inscribe commits are funded from the payment key only. */
	async signInscribe(psbt: string): Promise<string> {
		const tx = parsePsbtTransaction(psbt)
		const signed = await this.requestSignature(tx, 'balance', allInputIndexes(tx), [1])
		finalizeInputs(signed)
		return extractRawHex(signed)
	}

	async signMessage(message: string): Promise<string> {
		try {
			// @ts-ignore
			const res = await window.btc.request('signMessage', {
				message: message,
				paymentType: 'p2tr'
			})
			console.log({ res })
			return res.result.signature
		} catch (error: any) {
			console.error(error.message ?? 'An unknown error occurred. Please try again.')
			return undefined as unknown as string
		}
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		let signedSetup = ''
		let purchase = purchase_psbt

		if (setup_psbt) {
			const setupTx = parsePsbtTransaction(setup_psbt)
			const signed = await this.requestSignature(setupTx, 'balance', allInputIndexes(setupTx), [1])
			finalizeInputs(signed)
			signedSetup = extractRawHex(signed)
			// Re-point the purchase at the setup's SIGNED txid (chain.ts).
			const stale = inputTxidHex(parsePsbtTransaction(purchase_psbt), 0)
			purchase = linkChildToSignedParent(signedSetup, purchase_psbt, [stale])
		}

		const purchaseTx = parsePsbtTransaction(purchase)

		// The API funds a bulk purchase with N dummy inputs; only the first is
		// ours to sign, the rest are placeholders it finalizes itself.
		const dummyInputs = allInputIndexes(purchaseTx).filter(
			(i) => purchaseTx.getInput(i).witnessUtxo?.amount === DUMMY_INPUT_SATS
		).length
		const skipIndexes: number[] = []
		for (let i = 0; i < dummyInputs - 1; i++) {
			skipIndexes.push(i + dummyInputs)
		}
		const signingIndexes = allInputIndexes(purchaseTx).filter((i) => !skipIndexes.includes(i))

		const signed = await this.requestSignature(purchaseTx, 'balance', signingIndexes, [1, 0])
		finalizeInputs(signed, signingIndexes)
		return { signedSetup, signedPurchase: toPsbtHex(signed) }
	}

	/** The first `inscriptions` inputs are ordinals (taproot key); the rest pay the fee (payment key). */
	async signTransfer(psbt: string, inscriptions?: number): Promise<string> {
		let tx = parsePsbtTransaction(psbt)
		const all = allInputIndexes(tx)
		const ordinalIndexes = all.slice(0, inscriptions)
		const paymentIndexes = all.slice(inscriptions)

		if (ordinalIndexes.length) {
			tx = await this.requestSignature(tx, 'taproot', ordinalIndexes, [1])
			finalizeInputs(tx, ordinalIndexes)
		}
		if (paymentIndexes.length) {
			tx = await this.requestSignature(tx, 'balance', paymentIndexes, [1])
			finalizeInputs(tx, paymentIndexes)
		}
		return extractRawHex(tx)
	}

	/** Listing: every input is the seller's taproot, signed SINGLE|ANYONECANPAY. */
	async signEscrow(psbt: string): Promise<string> {
		const tx = parsePsbtTransaction(psbt)
		const signingIndexes = allInputIndexes(tx)
		const signed = await this.requestSignature(tx, 'taproot', signingIndexes, [131])
		finalizeTaprootKeyPath(signed, signingIndexes)
		return toPsbtHex(signed)
	}
}
